import { useEffect, useState, useCallback } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  AlertTriangle,
  AlertOctagon,
  PauseCircle,
  Copy,
  Check,
  Play,
  Pause,
  ExternalLink,
  RefreshCw,
  X,
} from 'lucide-react';

type SortBy = 'name' | 'records' | 'revenue' | 'cost' | 'created';
type SortDir = 'asc' | 'desc';

const DEFAULT_SORT: SortBy = 'created';
const DEFAULT_DIR: SortDir = 'desc';
const DEFAULT_LIMIT = 25;
const LIMIT_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;
const LIVE_REFRESH_INTERVAL_MS = 15_000;

const VALID_PIPELINE_STATES = [
  'all',
  'currently_processing',
  'in_phase_a',
  'in_phase_b',
  'in_tag_writes',
  'in_phase_c',
  'done',
  'failed',
  'counting_stuck',
  'activity_stuck',
  'spam_paused',
  'billing_paused',
  'user_paused',
] as const;
type PipelineState = typeof VALID_PIPELINE_STATES[number];
const DEFAULT_PIPELINE_STATE: PipelineState = 'all';

const PIPELINE_STATE_LABELS: Record<PipelineState, string> = {
  all: 'All',
  currently_processing: 'Currently processing (any phase)',
  in_phase_a: 'In Phase A — CSV validation',
  in_phase_b: 'In Phase B — landline/TCPA',
  in_tag_writes: 'GHL Tags pending — z.landline/z.tcpa upserts draining',
  in_phase_c: 'In Phase C — tagging',
  done: 'Done',
  failed: 'Failed',
  counting_stuck: 'Counting-stuck (transition never fired)',
  activity_stuck: 'Queue stalled (no activity)',
  spam_paused: 'Spam-paused',
  billing_paused: 'Billing-suspended',
  user_paused: 'User-paused',
};

function readInitialBatchListParams() {
  if (typeof window === 'undefined') {
    return {
      search: '',
      page: 1,
      sortBy: DEFAULT_SORT,
      sortDir: DEFAULT_DIR,
      limit: DEFAULT_LIMIT,
      locationId: '',
      pipelineState: DEFAULT_PIPELINE_STATE,
      startDate: '',
      endDate: '',
      live: false,
    };
  }
  const p = new URLSearchParams(window.location.search);
  const sortRaw = p.get('sort');
  const dirRaw = p.get('dir');
  const limitRaw = parseInt(p.get('limit') || '', 10);
  const validSorts: SortBy[] = ['name', 'records', 'revenue', 'cost', 'created'];
  const stateRaw = p.get('pipelineState');
  return {
    search: p.get('search') || '',
    page: Math.max(1, parseInt(p.get('page') || '1', 10) || 1),
    sortBy: (validSorts.includes(sortRaw as SortBy) ? (sortRaw as SortBy) : DEFAULT_SORT),
    sortDir: (dirRaw === 'asc' ? 'asc' : 'desc') as SortDir,
    limit: LIMIT_OPTIONS.includes(limitRaw) ? limitRaw : DEFAULT_LIMIT,
    locationId: p.get('locationId') || '',
    pipelineState: (VALID_PIPELINE_STATES.includes(stateRaw as PipelineState)
      ? (stateRaw as PipelineState)
      : DEFAULT_PIPELINE_STATE),
    startDate: p.get('startDate') || '',
    endDate: p.get('endDate') || '',
    live: p.get('live') === 'true',
  };
}

interface PipelineHealth {
  in_phase_a: number;
  in_phase_b: number;
  in_tag_writes: number;
  in_phase_c: number;
  done: number;
  failed: number;
  failed_last_24h: number;
  spam_paused: number;
  billing_paused: number;
  user_paused: number;
  counting_stuck: number;
  activity_stuck: number;
  threshold_seconds: number;
}

interface Batch {
  id: string;
  batch_name: string;
  ghl_location_id: string;
  location_name?: string;
  status: string;
  original_filename?: string | null;
  file_size_bytes?: number | null;
  total_rows_in_csv?: number;
  total_contacts?: number;
  total_records?: number; // legacy field name, kept for old detail modal
  needs_processing_count: number;
  processed_count: number;
  error_count: number;
  invalid_count?: number;
  duplicate_count?: number;
  known_landline_count?: number;
  known_tcpa_count?: number;
  already_processing_count?: number;
  landline_count?: number;
  tcpa_count?: number;
  clean_count?: number;
  messages_errored_count?: number;
  estimated_cost: number;
  actual_cost: number;
  estimated_revenue: number;
  actual_revenue: number;
  payment_transaction_id: string | null;
  created_at: string;
  upload_completed_at?: string | null;
  payment_received_at?: string | null;
  processing_started_at?: string | null;
  processing_completed_at: string | null;
  // Messaging fields for status display
  messages_sent_at?: string | null;
  messaging_scheduled_for?: string | null;
  messaging_started_at?: string | null;
  tags_applied_count?: number;
  // Pause state
  spam_paused_at?: string | null;
  billing_paused_at?: string | null;
  spam_paused?: boolean;
  billing_paused?: boolean;
  user_paused?: boolean;
  // Counting-stuck flags (no time threshold)
  counting_stuck_a?: boolean;
  counting_stuck_b?: boolean;
  counting_stuck_c?: boolean;
  // Activity-stuck flags (per (location, phase) > N seconds)
  activity_stuck_a?: boolean;
  activity_stuck_b?: boolean;
  activity_stuck_c?: boolean;
  // Per-location last activity per phase (for hover tooltips)
  phase_a_location_last_activity?: string | null;
  phase_b_location_last_activity?: string | null;
  phase_c_location_last_activity?: string | null;
  activity_stuck_threshold_seconds?: number;
  // Phase 4: GHL tag-write progress (z.landline / z.tcpa upserts for this batch's bad contacts)
  tag_writes?: {
    queued: number;
    processing: number;
    completed: number;
    failed: number;
    total: number;
  };
}

interface TagWriteRow {
  id: string;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  tag: string;
  classification: string;
  source: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  retry_count: number;
  last_error: string | null;
  ghl_contact_id: string | null;
  created_at: string;
  processed_at: string | null;
}

interface TagWritesResponse {
  batchId: string;
  tagWrites: TagWriteRow[];
  counts: { queued: number; processing: number; completed: number; failed: number };
  truncated: boolean;
}

type PhaseKey = 'a' | 'b' | 'c';
type PhaseState = 'pending' | 'running' | 'done' | 'counting_stuck' | 'activity_stuck' | 'paused';

interface PhaseSummary {
  key: PhaseKey;
  label: string;
  state: PhaseState;
  // For tooltip / micro-display
  totalLabel: string;
  done: number;
  total: number;
  errored: number;
  pausedCount?: number;
}

/**
 * Derive the visual state of each pipeline phase from the batch flags + counts.
 * Pause states are checked first because they override stuck/running (a
 * spam-paused batch isn't "stuck" — it's intentionally halted).
 */
function derivePhaseSummary(batch: Batch): PhaseSummary[] {
  const cleanCount = batch.clean_count ?? 0;
  const tagsApplied = batch.tags_applied_count ?? 0;
  const tagsErrored = batch.messages_errored_count ?? 0;
  const totalContacts = batch.total_contacts ?? 0;
  const needsProc = batch.needs_processing_count ?? 0;
  const processed = batch.processed_count ?? 0;
  const errors = batch.error_count ?? 0;
  const totalCsv = batch.total_rows_in_csv ?? 0;

  // Phase A — CSV validation
  let stateA: PhaseState;
  if (batch.upload_completed_at) {
    stateA = 'done';
  } else if (batch.counting_stuck_a) {
    stateA = 'counting_stuck';
  } else if (batch.spam_paused || batch.billing_paused) {
    stateA = 'paused';
  } else if (batch.activity_stuck_a) {
    stateA = 'activity_stuck';
  } else if (totalCsv > 0) {
    stateA = 'running';
  } else {
    stateA = 'pending';
  }

  // Phase B — landline/TCPA processing
  let stateB: PhaseState;
  if (batch.processing_completed_at) {
    stateB = 'done';
  } else if (batch.counting_stuck_b) {
    stateB = 'counting_stuck';
  } else if (batch.spam_paused || batch.billing_paused) {
    stateB = 'paused';
  } else if (batch.activity_stuck_b) {
    stateB = 'activity_stuck';
  } else if (batch.processing_started_at) {
    stateB = 'running';
  } else {
    stateB = 'pending';
  }

  // Phase C — tagging in GHL
  let stateC: PhaseState;
  if (batch.messages_sent_at) {
    stateC = 'done';
  } else if (batch.counting_stuck_c) {
    stateC = 'counting_stuck';
  } else if (batch.spam_paused || batch.billing_paused || batch.user_paused) {
    stateC = 'paused';
  } else if (batch.activity_stuck_c) {
    stateC = 'activity_stuck';
  } else if (batch.messaging_started_at) {
    stateC = 'running';
  } else {
    stateC = 'pending';
  }

  return [
    {
      key: 'a',
      label: 'A',
      state: stateA,
      totalLabel: 'CSV rows',
      done: totalContacts, // rows that have made it through validation into contacts_to_process
      total: totalCsv,
      errored: batch.invalid_count ?? 0,
    },
    {
      key: 'b',
      label: 'B',
      state: stateB,
      totalLabel: 'contacts',
      done: processed,
      total: needsProc,
      errored: errors,
    },
    {
      key: 'c',
      label: 'C',
      state: stateC,
      totalLabel: 'tags',
      done: tagsApplied,
      total: cleanCount,
      errored: tagsErrored,
    },
  ];
}

function phaseSegmentClass(state: PhaseState): string {
  switch (state) {
    case 'done':
      return 'bg-emerald-500';
    case 'running':
      return 'bg-blue-500';
    case 'counting_stuck':
      return 'bg-red-500';
    case 'activity_stuck':
      return 'bg-amber-500';
    case 'paused':
      return 'bg-yellow-400';
    case 'pending':
    default:
      return 'bg-gray-200';
  }
}

function phaseStateLabel(state: PhaseState): string {
  switch (state) {
    case 'done': return 'Done';
    case 'running': return 'Running';
    case 'counting_stuck': return 'Counting-stuck';
    case 'activity_stuck': return 'Queue stalled';
    case 'paused': return 'Paused';
    case 'pending': return 'Pending';
  }
}

/**
 * Compact 2-decimal money format for the list row. Use 4-decimal in the
 * detail modal where precision matters more than scannability.
 */
function formatMoney2(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format an absolute date+time the same way across the list and modal.
 */
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format an elapsed duration between two timestamps. Used by the phase
 * timeline. Returns "instant" for sub-second gaps.
 */
function formatElapsedBetween(fromIso: string | null | undefined, toIso: string | null | undefined): string {
  if (!fromIso || !toIso) return '';
  const from = new Date(fromIso).getTime();
  const to = new Date(toIso).getTime();
  if (isNaN(from) || isNaN(to)) return '';
  const seconds = Math.max(0, Math.floor((to - from) / 1000));
  if (seconds < 1) return 'instant';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const remS = seconds % 60;
  if (mins < 60) return remS > 0 && mins < 10 ? `${mins}m ${remS}s` : `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remM = mins % 60;
  if (hours < 24) return remM > 0 ? `${hours}h ${remM}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remH = hours % 24;
  return remH > 0 ? `${days}d ${remH}h` : `${days}d`;
}

/**
 * Human-readable byte size (KB / MB / GB) for the detail modal's CSV info.
 */
function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'never';
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface Location {
  id: string;
  name: string;
  batchCount: number;
}

export default function AdminBatches() {
  const initialParams = readInitialBatchListParams();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [locations, setLocations] = useState<Location[]>([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState(initialParams.search);
  const [debouncedSearch, setDebouncedSearch] = useState(initialParams.search);
  const [selectedLocation, setSelectedLocation] = useState(initialParams.locationId);
  const [pipelineState, setPipelineState] = useState<PipelineState>(initialParams.pipelineState);
  const [startDate, setStartDate] = useState(initialParams.startDate);
  const [endDate, setEndDate] = useState(initialParams.endDate);

  // Pagination + sort (1-indexed)
  const [page, setPage] = useState(initialParams.page);
  const [pageInput, setPageInput] = useState<string>(String(initialParams.page));
  const [limit, setLimit] = useState<number>(initialParams.limit);
  const [sortBy, setSortBy] = useState<SortBy>(initialParams.sortBy);
  const [sortDir, setSortDir] = useState<SortDir>(initialParams.sortDir);

  // Health header counts
  const [health, setHealth] = useState<PipelineHealth | null>(null);

  // Live auto-refresh toggle (15s polling)
  const [live, setLive] = useState<boolean>(initialParams.live);

  // Selected batch for detail view
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  // Phase 4: GHL tag-writes detail modal
  const [tagWritesModalBatch, setTagWritesModalBatch] = useState<Batch | null>(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  // Search-on-type with debounce so we don't fire a request on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    fetchBatches();
  }, [page, selectedLocation, pipelineState, startDate, endDate, debouncedSearch, sortBy, sortDir, limit]);

  // Sync state to URL so refresh/back/forward/bookmarks survive.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams();
    if (debouncedSearch) p.set('search', debouncedSearch);
    if (page !== 1) p.set('page', String(page));
    if (sortBy !== DEFAULT_SORT) p.set('sort', sortBy);
    if (sortDir !== DEFAULT_DIR) p.set('dir', sortDir);
    if (limit !== DEFAULT_LIMIT) p.set('limit', String(limit));
    if (selectedLocation) p.set('locationId', selectedLocation);
    if (pipelineState !== DEFAULT_PIPELINE_STATE) p.set('pipelineState', pipelineState);
    if (startDate) p.set('startDate', startDate);
    if (endDate) p.set('endDate', endDate);
    if (live) p.set('live', 'true');
    const qs = p.toString();
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState(null, '', url);
  }, [debouncedSearch, page, sortBy, sortDir, limit, selectedLocation, pipelineState, startDate, endDate, live]);

  // Keep the page input box in sync when page changes via clicks.
  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/locations`, {
        credentials: 'include',
      });
      const data = await response.json() as any;
      if (data.success) {
        setLocations(data.locations || []);
      }
    } catch (error) {
      console.error('[AdminBatches] Error fetching locations:', error);
    }
  };

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String((page - 1) * limit),
        sort: sortBy,
        dir: sortDir,
      });

      if (selectedLocation) params.set('locationId', selectedLocation);
      if (pipelineState !== DEFAULT_PIPELINE_STATE) params.set('pipelineState', pipelineState);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (debouncedSearch) params.set('search', debouncedSearch);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/batches?${params.toString()}`,
        { credentials: 'include' }
      );
      const data = await response.json() as any;

      if (data.success) {
        setBatches(data.batches || []);
        setTotalCount(data.totalCount || 0);
      }
    } catch (error) {
      console.error('[AdminBatches] Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, sortDir, selectedLocation, pipelineState, startDate, endDate, debouncedSearch]);

  // Fetch system-wide pipeline health counts for the header strip. Refreshed
  // whenever the list refetches so the tiles stay roughly in sync.
  const fetchHealth = useCallback(async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/batches/health`,
        { credentials: 'include' }
      );
      const data = await response.json() as { success: boolean; health: PipelineHealth };
      if (data.success && data.health) {
        setHealth(data.health);
      }
    } catch (error) {
      console.error('[AdminBatches] Health fetch error:', error);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth, debouncedSearch, page, sortBy, sortDir, limit, selectedLocation, pipelineState, startDate, endDate]);

  // Live auto-refresh: when toggled on, re-fetch both the list and the health
  // header on a 15s cadence. Cleanup on unmount or when the toggle flips off
  // so we don't leak intervals.
  useEffect(() => {
    if (!live) return;
    const handle = setInterval(() => {
      fetchBatches();
      fetchHealth();
    }, LIVE_REFRESH_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [live, fetchBatches, fetchHealth]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedLocation('');
    setPipelineState(DEFAULT_PIPELINE_STATE);
    setStartDate('');
    setEndDate('');
    setSortBy(DEFAULT_SORT);
    setSortDir(DEFAULT_DIR);
    setLimit(DEFAULT_LIMIT);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const hasFilters = searchTerm || selectedLocation || pipelineState !== DEFAULT_PIPELINE_STATE || startDate || endDate
    || sortBy !== DEFAULT_SORT || sortDir !== DEFAULT_DIR || limit !== DEFAULT_LIMIT;

  // ============================================================
  // Sort + pagination helpers
  // ============================================================
  const goToPage = (n: number) => {
    const clamped = Math.max(1, Math.min(totalPages, Math.floor(n) || 1));
    setPage(clamped);
  };

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      // Sensible default direction per column: text=asc, numeric/date=desc
      setSortDir(column === 'name' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const submitPageInput = () => {
    const n = parseInt(pageInput, 10);
    if (Number.isFinite(n) && n >= 1) {
      goToPage(n);
    } else {
      setPageInput(String(page));
    }
  };

  const renderSortHeader = (
    column: SortBy,
    label: string,
    align: 'left' | 'right' = 'left'
  ) => {
    const active = sortBy === column;
    const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th
        onClick={() => handleSort(column)}
        className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 ${
          align === 'right' ? 'text-right' : 'text-left'
        }`}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
          {label}
          <Icon className={`w-3.5 h-3.5 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
        </span>
      </th>
    );
  };

  const renderPaginationBar = (position: 'top' | 'bottom') => (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${
        position === 'top'
          ? 'bg-gray-50 border-b border-gray-200'
          : 'bg-gray-50 border-t border-gray-200'
      }`}
    >
      <div className="flex items-center gap-3 text-sm text-gray-600">
        <span>
          {totalCount > 0
            ? `Showing ${(page - 1) * limit + 1} to ${Math.min(page * limit, totalCount)} of ${totalCount} batches`
            : 'No batches'}
        </span>
        <span className="text-gray-300">|</span>
        <label className="flex items-center gap-2">
          Rows:
          <select
            value={limit}
            onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
            className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
          >
            {LIMIT_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => goToPage(1)}
          disabled={page <= 1}
          title="First page"
          aria-label="First page"
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronsLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => goToPage(page - 1)}
          disabled={page <= 1}
          title="Previous page"
          aria-label="Previous page"
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-sm text-gray-600 px-2 flex items-center gap-1">
          Page
          <input
            type="text"
            inputMode="numeric"
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
            onBlur={submitPageInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                (e.currentTarget as HTMLInputElement).blur();
              }
            }}
            className="w-12 px-1.5 py-1 text-center border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
            aria-label="Page number"
          />
          of {totalPages}
        </span>
        <button
          onClick={() => goToPage(page + 1)}
          disabled={page >= totalPages}
          title="Next page"
          aria-label="Next page"
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <button
          onClick={() => goToPage(totalPages)}
          disabled={page >= totalPages}
          title="Last page"
          aria-label="Last page"
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronsRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Pipeline Health Header — clickable tiles apply the matching pipeline_state filter. */}
      <HealthHeader
        health={health}
        active={pipelineState}
        onTileClick={(state: PipelineState) => {
          setPipelineState(state);
          setPage(1);
        }}
      />

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search batch name..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Location Filter */}
          <select
            value={selectedLocation}
            onChange={(e) => {
              setSelectedLocation(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Locations</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name} ({loc.batchCount})
              </option>
            ))}
          </select>

          {/* Pipeline State Filter — replaces old status dropdown + processingOnly checkbox */}
          <select
            value={pipelineState}
            onChange={(e) => {
              setPipelineState(e.target.value as PipelineState);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            title="Pipeline state"
          >
            <option value="all">All</option>
            <optgroup label="Active">
              <option value="currently_processing">{PIPELINE_STATE_LABELS.currently_processing}</option>
              <option value="in_phase_a">{PIPELINE_STATE_LABELS.in_phase_a}</option>
              <option value="in_phase_b">{PIPELINE_STATE_LABELS.in_phase_b}</option>
              <option value="in_tag_writes">{PIPELINE_STATE_LABELS.in_tag_writes}</option>
              <option value="in_phase_c">{PIPELINE_STATE_LABELS.in_phase_c}</option>
            </optgroup>
            <optgroup label="Terminal">
              <option value="done">{PIPELINE_STATE_LABELS.done}</option>
              <option value="failed">{PIPELINE_STATE_LABELS.failed}</option>
            </optgroup>
            <optgroup label="Issue">
              <option value="counting_stuck">{PIPELINE_STATE_LABELS.counting_stuck}</option>
              <option value="activity_stuck">{PIPELINE_STATE_LABELS.activity_stuck}</option>
              <option value="spam_paused">{PIPELINE_STATE_LABELS.spam_paused}</option>
              <option value="billing_paused">{PIPELINE_STATE_LABELS.billing_paused}</option>
              <option value="user_paused">{PIPELINE_STATE_LABELS.user_paused}</option>
            </optgroup>
          </select>

          {/* Date Range */}
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                const newStart = e.target.value;
                setStartDate(newStart);
                if (newStart > endDate) {
                  setEndDate(newStart);
                }
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                const newEnd = e.target.value;
                setEndDate(newEnd);
                if (newEnd < startDate) {
                  setStartDate(newEnd);
                }
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Clear Filters */}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </button>
          )}

          {/* Live auto-refresh toggle */}
          <button
            onClick={() => setLive((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
              live
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            title={live ? `Live mode on — refreshing every ${LIVE_REFRESH_INTERVAL_MS / 1000}s` : `Turn on live mode (auto-refresh every ${LIVE_REFRESH_INTERVAL_MS / 1000}s)`}
          >
            {live ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            <span>{live ? 'Live' : 'Live off'}</span>
            {live && (
              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
            )}
          </button>

          {/* Refresh */}
          <button
            onClick={() => { fetchBatches(); fetchHealth(); }}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            title="Refresh now"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {renderPaginationBar('top')}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {renderSortHeader('name', 'Batch', 'left')}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                {renderSortHeader('records', 'Pipeline', 'left')}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="GHL tag-write progress for this batch's bad contacts (z.landline / z.tcpa). Runs in parallel with Phase C — does not gate batch completion.">
                  GHL Tags
                </th>
                {renderSortHeader('revenue', 'Revenue', 'left')}
                {renderSortHeader('cost', 'Cost', 'left')}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profit
                </th>
                {renderSortHeader('created', 'Created', 'left')}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="ml-2 text-gray-500">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : batches.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    No batches found
                  </td>
                </tr>
              ) : (
                batches.map((batch) => {
                  const revenue = parseFloat(String(batch.actual_revenue)) || 0;
                  const cost = parseFloat(String(batch.actual_cost)) || 0;
                  const profit = revenue - cost;

                  return (
                    <tr
                      key={batch.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedBatch(batch)}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[220px]" title={batch.batch_name}>
                            {batch.batch_name}
                          </p>
                          {batch.original_filename && batch.original_filename !== batch.batch_name && (
                            <p className="text-xs text-gray-400 truncate max-w-[220px]" title={batch.original_filename}>
                              {batch.original_filename}
                            </p>
                          )}
                          <CopyableBatchId id={batch.id} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900 truncate max-w-[150px]">
                          {batch.location_name || 'Unknown'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge batch={batch} />
                      </td>
                      <td className="px-4 py-3">
                        <PipelineCell batch={batch} />
                      </td>
                      <td
                        className="px-4 py-3"
                        onClick={(e) => {
                          // Don't trigger the row's batch-detail modal when
                          // the operator clicks the tag-writes cell
                          e.stopPropagation();
                          if ((batch.tag_writes?.total ?? 0) > 0) {
                            setTagWritesModalBatch(batch);
                          }
                        }}
                      >
                        <TagWritesCell batch={batch} />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-green-600 whitespace-nowrap">
                        {formatMoney2(revenue)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatMoney2(cost)}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatMoney2(profit)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {formatDateTime(batch.created_at)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {renderPaginationBar('bottom')}
      </div>

      {/* Batch Detail Modal */}
      {selectedBatch && (
        <BatchDetailModal
          batch={selectedBatch}
          onClose={() => setSelectedBatch(null)}
        />
      )}

      {/* Phase 4: GHL Tag Writes Detail Modal */}
      {tagWritesModalBatch && (
        <TagWritesModal
          batch={tagWritesModalBatch}
          onClose={() => setTagWritesModalBatch(null)}
        />
      )}
    </div>
  );
}

/**
 * Get display status text based on batch status and messaging fields
 * Mirrors logic from user-facing BatchList.tsx
 */
function getStatusText(batch: Batch): string {
  const { status, messages_sent_at, messaging_scheduled_for, tags_applied_count } = batch;

  // Check if batch has messaging data
  const hasMessagingData = messages_sent_at || (tags_applied_count && tags_applied_count > 0);

  if (status === 'completed' && hasMessagingData) {
    const now = new Date();

    // Ensure UTC timestamps are properly parsed by adding Z if missing
    const scheduledForStr = messaging_scheduled_for
      ? (messaging_scheduled_for.endsWith('Z') ? messaging_scheduled_for : messaging_scheduled_for + 'Z')
      : null;
    const sentAtStr = messages_sent_at
      ? (messages_sent_at.endsWith('Z') ? messages_sent_at : messages_sent_at + 'Z')
      : null;
    const scheduledFor = scheduledForStr ? new Date(scheduledForStr) : null;
    const sentAt = sentAtStr ? new Date(sentAtStr) : null;

    // Messages have been sent - show when they started
    if (sentAt) {
      const startedAtStr = batch.messaging_started_at
        ? (batch.messaging_started_at.endsWith('Z') ? batch.messaging_started_at : batch.messaging_started_at + 'Z')
        : scheduledForStr;
      const startedAt = startedAtStr ? new Date(startedAtStr) : null;
      if (startedAt && !isNaN(startedAt.getTime())) {
        const timeStr = startedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
        return `Messages Sent Starting At ${timeStr}`;
      }
      return 'Messages Sent';
    }

    // Messaging is in progress (after scheduled time)
    if (scheduledFor && now >= scheduledFor) {
      return 'Sending Messages...';
    }

    // Scheduled for future
    if (scheduledFor && now < scheduledFor) {
      return `Scheduled ${scheduledFor.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })} ${scheduledFor.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    }
  }

  // Fall back to basic status formatting
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'processing':
      return 'Processing';
    case 'failed':
      return 'Failed';
    case 'pending':
      return 'Pending';
    case 'upload_complete':
      return 'Upload Complete';
    default:
      return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }
}

// Status Badge Component
function StatusBadge({ batch }: { batch: Batch }) {
  const statusText = getStatusText(batch);

  const getStatusStyles = () => {
    // Check for messaging-related statuses first
    if (statusText.startsWith('Messages Sent')) {
      return 'bg-emerald-100 text-emerald-800';
    }
    if (statusText === 'Sending Messages...') {
      return 'bg-blue-100 text-blue-800';
    }
    if (statusText.startsWith('Scheduled')) {
      return 'bg-purple-100 text-purple-800';
    }

    // Fall back to status-based styling
    switch (batch.status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'upload_complete':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusStyles()}`}>
      {statusText}
    </span>
  );
}

/**
 * Small mono pill showing the first 8 chars of a batch ID with a copy icon.
 * Click copies the full UUID to the clipboard; the icon briefly switches to
 * a check mark. `stopPropagation` prevents the parent row's onClick from
 * also opening the detail modal.
 */
function CopyableBatchId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Clipboard API not available — silently ignore */
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Click to copy full ID: ${id}`}
      className="inline-flex items-center gap-1 text-[10px] font-mono text-gray-500 hover:text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors mt-0.5"
    >
      <span>{id.substring(0, 8)}</span>
      {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

/**
 * Pipeline health header strip. Shows system-wide counts for each pipeline
 * state. Clicking a tile applies that state as the filter so admin can
 * drill in instantly. The active tile is highlighted.
 */
function HealthHeader({
  health,
  active,
  onTileClick,
}: {
  health: PipelineHealth | null;
  active: PipelineState;
  onTileClick: (state: PipelineState) => void;
}) {
  if (!health) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 text-sm text-gray-400">
        Loading pipeline health…
      </div>
    );
  }

  type Tile = {
    state: PipelineState;
    label: string;
    value: number;
    tone: 'neutral' | 'info' | 'success' | 'warn' | 'danger';
    title?: string;
  };

  const tiles: Tile[] = [
    { state: 'in_phase_a', label: 'Phase A', value: health.in_phase_a, tone: 'info' },
    { state: 'in_phase_b', label: 'Phase B', value: health.in_phase_b, tone: 'info' },
    // Phase 4: side-stage tile — batches with GHL tag writes still queued or
    // processing. Runs in parallel with the rest of the pipeline; does NOT gate
    // Phase B completion (intentional per design).
    { state: 'in_tag_writes', label: 'GHL Tags', value: health.in_tag_writes, tone: 'info', title: 'Batches with z.landline / z.tcpa upserts still queued or processing in the GHLTagWriteWorkflow. Side-stage that runs in parallel with Phase C.' },
    { state: 'in_phase_c', label: 'Phase C', value: health.in_phase_c, tone: 'info' },
    { state: 'done', label: 'Done', value: health.done, tone: 'success' },
    { state: 'failed', label: `Failed (24h: ${health.failed_last_24h})`, value: health.failed, tone: 'danger', title: `${health.failed} total failed; ${health.failed_last_24h} in the last 24 hours` },
    { state: 'counting_stuck', label: 'Counting-stuck', value: health.counting_stuck, tone: 'danger', title: 'Counts say done but the transition flag never fired. CRON should have healed these but didn\'t.' },
    { state: 'activity_stuck', label: `Queue stalled (>${health.threshold_seconds}s)`, value: health.activity_stuck, tone: 'warn', title: `Per-(location, phase) queues that haven't moved a contact in more than ${health.threshold_seconds}s` },
    { state: 'spam_paused', label: 'Spam-paused', value: health.spam_paused, tone: 'warn' },
    { state: 'billing_paused', label: 'Billing-suspended', value: health.billing_paused, tone: 'warn' },
    { state: 'user_paused', label: 'User-paused', value: health.user_paused, tone: 'neutral' },
  ];

  const toneClasses: Record<Tile['tone'], { idle: string; active: string }> = {
    neutral: { idle: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100',  active: 'bg-gray-200 text-gray-900 border-gray-400' },
    info:    { idle: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',  active: 'bg-blue-200 text-blue-900 border-blue-400' },
    success: { idle: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100', active: 'bg-emerald-200 text-emerald-900 border-emerald-400' },
    warn:    { idle: 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100', active: 'bg-amber-200 text-amber-900 border-amber-400' },
    danger:  { idle: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',     active: 'bg-red-200 text-red-900 border-red-400' },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
      <div className="flex flex-wrap items-stretch gap-2">
        <button
          type="button"
          onClick={() => onTileClick('all')}
          title="Clear pipeline-state filter"
          className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
            active === 'all'
              ? 'bg-gray-900 text-white border-gray-900'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          All
        </button>
        {tiles.map((t) => {
          const cls = toneClasses[t.tone];
          const isActive = active === t.state;
          return (
            <button
              key={t.state}
              type="button"
              onClick={() => onTileClick(t.state)}
              title={t.title || `Filter to ${t.label.toLowerCase()}`}
              className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors min-w-[90px] text-left ${isActive ? cls.active : cls.idle}`}
            >
              <div className="text-[10px] uppercase tracking-wider opacity-80">{t.label}</div>
              <div className="text-base font-bold leading-tight">{t.value.toLocaleString()}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compact pipeline visualization for a single batch.
 * Shows 3 small colored segments (one per phase) + status icons.
 * Hover any segment for state + counts; hover the icons for the
 * specific stuck/pause reason.
 */
function PipelineCell({ batch }: { batch: Batch }) {
  const phases = derivePhaseSummary(batch);
  const threshold = batch.activity_stuck_threshold_seconds ?? 90;

  const hasCountingStuck = phases.some((p) => p.state === 'counting_stuck');
  const hasActivityStuck = phases.some((p) => p.state === 'activity_stuck');
  const hasPaused = phases.some((p) => p.state === 'paused');

  // Build tooltip text for each icon
  const countingStuckPhases = phases.filter((p) => p.state === 'counting_stuck').map((p) => p.label).join(', ');
  const activityStuckPhases = phases.filter((p) => p.state === 'activity_stuck').map((p) => p.label).join(', ');

  let pauseReason = '';
  if (batch.spam_paused) pauseReason = 'Spam-paused';
  else if (batch.billing_paused) pauseReason = 'Billing-suspended';
  else if (batch.user_paused) pauseReason = 'User-paused (contact level)';

  // Activity-stuck tooltip includes the location's last activity timestamp
  // per phase so admin can see exactly which queue is quiet.
  const stalledDetails = phases
    .filter((p) => p.state === 'activity_stuck')
    .map((p) => {
      const lastActivity =
        p.key === 'a' ? batch.phase_a_location_last_activity :
        p.key === 'b' ? batch.phase_b_location_last_activity :
        batch.phase_c_location_last_activity;
      return `Phase ${p.label}: location's queue last active ${formatRelative(lastActivity)}`;
    })
    .join('\n');

  return (
    <div className="flex items-center gap-2 min-w-[200px]">
      {/* 3-segment phase bar */}
      <div className="flex items-center gap-0.5">
        {phases.map((p) => {
          const tooltip = `Phase ${p.label}: ${phaseStateLabel(p.state)}\n` +
            (p.total > 0
              ? `${p.done.toLocaleString()} / ${p.total.toLocaleString()} ${p.totalLabel}` +
                (p.errored > 0 ? `\n${p.errored.toLocaleString()} errors` : '')
              : 'no work');
          return (
            <div
              key={p.key}
              title={tooltip}
              className={`w-7 h-5 rounded text-[10px] font-semibold text-white flex items-center justify-center ${phaseSegmentClass(p.state)}`}
            >
              {p.label}
            </div>
          );
        })}
      </div>

      {/* Status icons */}
      <div className="flex items-center gap-1">
        {hasCountingStuck && (
          <span
            title={`Counting-stuck — Phase ${countingStuckPhases}: counts say done but transition flag never fired. CRON should have healed this but didn't.`}
            className="text-red-600"
            aria-label="Counting-stuck"
          >
            <AlertOctagon className="w-4 h-4" />
          </span>
        )}
        {hasActivityStuck && (
          <span
            title={`Queue stalled (Phase ${activityStuckPhases}) — no activity in >${threshold}s${stalledDetails ? '\n' + stalledDetails : ''}`}
            className="text-amber-600"
            aria-label="Activity-stuck"
          >
            <AlertTriangle className="w-4 h-4" />
          </span>
        )}
        {hasPaused && pauseReason && (
          <span
            title={pauseReason}
            className="text-yellow-600"
            aria-label={pauseReason}
          >
            <PauseCircle className="w-4 h-4" />
          </span>
        )}
      </div>

      {/* Compact text summary */}
      <div className="text-xs text-gray-500 ml-auto whitespace-nowrap">
        {(() => {
          // Show the most informative count: the currently-running phase, or
          // the last phase that ran. Falls back to total contacts.
          const running = phases.find((p) => p.state === 'running' || p.state === 'activity_stuck' || p.state === 'counting_stuck');
          if (running && running.total > 0) {
            return (
              <span>
                {running.done.toLocaleString()}/{running.total.toLocaleString()}
                {running.errored > 0 && (
                  <span className="text-red-600 ml-1">+{running.errored}e</span>
                )}
              </span>
            );
          }
          if ((batch.total_contacts ?? 0) > 0) {
            return <span>{(batch.total_contacts ?? 0).toLocaleString()} contacts</span>;
          }
          return null;
        })()}
      </div>
    </div>
  );
}

/**
 * Detail modal for a single batch. Shows everything an admin would otherwise
 * have to query the DB for: phase-by-phase timeline with elapsed times, per-
 * phase count breakdowns, pause reasons (with timestamps), CSV metadata,
 * financials, and payment info.
 */
function BatchDetailModal({
  batch,
  onClose,
}: {
  batch: Batch;
  onClose: () => void;
}) {
  const revenue = parseFloat(String(batch.actual_revenue)) || 0;
  const cost = parseFloat(String(batch.actual_cost)) || 0;
  const profit = revenue - cost;
  const estRevenue = parseFloat(String(batch.estimated_revenue)) || 0;
  const estCost = parseFloat(String(batch.estimated_cost)) || 0;
  const phases = derivePhaseSummary(batch);

  const hasAnyPause = batch.spam_paused || batch.billing_paused || batch.user_paused;

  // Timeline steps — labels and the timestamp accessor on the batch. Each
  // step shows its absolute time + the elapsed since the previous SET step.
  const steps: Array<{ label: string; ts: string | null | undefined; key: string }> = [
    { key: 'created', label: 'Created', ts: batch.created_at },
    { key: 'upload_completed_at', label: 'Phase A — Upload complete', ts: batch.upload_completed_at },
    { key: 'payment_received_at', label: 'Payment received', ts: batch.payment_received_at },
    { key: 'processing_started_at', label: 'Phase B — Processing started', ts: batch.processing_started_at },
    { key: 'processing_completed_at', label: 'Phase B — Processing complete', ts: batch.processing_completed_at },
    { key: 'messaging_scheduled_for', label: 'Messaging scheduled for', ts: batch.messaging_scheduled_for },
    { key: 'messaging_started_at', label: 'Phase C — Messaging started', ts: batch.messaging_started_at },
    { key: 'messages_sent_at', label: 'Phase C — Messages sent', ts: batch.messages_sent_at },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">Batch Details</h2>
            <StatusBadge batch={batch} />
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Identification */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Identification</h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoItem label="Batch Name" value={batch.batch_name} />
              <div>
                <p className="text-xs text-gray-500">Batch ID</p>
                <CopyableBatchId id={batch.id} />
              </div>
              <InfoItem label="Location" value={batch.location_name || batch.ghl_location_id} />
              <InfoItem label="Raw status" value={batch.status} mono />
            </div>
          </div>

          {/* Pipeline Timeline */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Pipeline Timeline</h3>
            <ol className="relative border-l-2 border-gray-200 ml-2 space-y-3">
              {steps.map((step, idx) => {
                const set = !!step.ts;
                // Find the previous SET step before this one to compute elapsed
                let prevTs: string | null | undefined = null;
                for (let i = idx - 1; i >= 0; i--) {
                  if (steps[i].ts) { prevTs = steps[i].ts; break; }
                }
                const elapsed = set && prevTs ? formatElapsedBetween(prevTs, step.ts) : '';
                return (
                  <li key={step.key} className="ml-4">
                    <div
                      className={`absolute -left-[7px] w-3 h-3 rounded-full border-2 ${
                        set ? 'bg-blue-500 border-blue-500' : 'bg-gray-200 border-gray-300'
                      }`}
                    />
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${set ? 'text-gray-900' : 'text-gray-400'}`}>
                        {step.label}
                      </span>
                      {elapsed && (
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider">+{elapsed}</span>
                      )}
                    </div>
                    <p className={`text-xs ${set ? 'text-gray-600' : 'text-gray-400'}`}>
                      {set ? formatDateTime(step.ts) : 'not yet'}
                    </p>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Phase A — CSV Validation */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Phase A — CSV Validation
              <PhaseStatePill state={phases[0].state} />
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <InfoItem label="CSV rows" value={(batch.total_rows_in_csv ?? 0).toLocaleString()} />
              <InfoItem label="Validated → contacts" value={(batch.total_contacts ?? 0).toLocaleString()} />
              <InfoItem label="Invalid" value={(batch.invalid_count ?? 0).toLocaleString()} />
              <InfoItem label="Duplicate" value={(batch.duplicate_count ?? 0).toLocaleString()} />
              <InfoItem label="Known landline" value={(batch.known_landline_count ?? 0).toLocaleString()} />
              <InfoItem label="Known TCPA" value={(batch.known_tcpa_count ?? 0).toLocaleString()} />
              <InfoItem label="Already processing" value={(batch.already_processing_count ?? 0).toLocaleString()} />
              <InfoItem label="Needs processing (Phase B target)" value={(batch.needs_processing_count ?? 0).toLocaleString()} />
            </div>
          </div>

          {/* Phase B — Landline/TCPA */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Phase B — Landline / TCPA
              <PhaseStatePill state={phases[1].state} />
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <InfoItem label="Processed" value={(batch.processed_count ?? 0).toLocaleString()} />
              <InfoItem label="Errors" value={(batch.error_count ?? 0).toLocaleString()} className={(batch.error_count ?? 0) > 0 ? 'text-red-600' : ''} />
              <InfoItem label="Clean (Phase C target)" value={(batch.clean_count ?? 0).toLocaleString()} className="text-emerald-600" />
              <InfoItem label="Landline found" value={(batch.landline_count ?? 0).toLocaleString()} />
              <InfoItem label="TCPA found" value={(batch.tcpa_count ?? 0).toLocaleString()} />
            </div>
          </div>

          {/* Phase C — Tagging */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Phase C — Tagging
              <PhaseStatePill state={phases[2].state} />
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <InfoItem label="Tags applied" value={(batch.tags_applied_count ?? 0).toLocaleString()} className="text-emerald-600" />
              <InfoItem label="Tagging errors" value={(batch.messages_errored_count ?? 0).toLocaleString()} className={(batch.messages_errored_count ?? 0) > 0 ? 'text-red-600' : ''} />
              <InfoItem label="Target (clean count)" value={(batch.clean_count ?? 0).toLocaleString()} />
            </div>
          </div>

          {/* Pause / blocked state — only when relevant */}
          {hasAnyPause && (
            <div>
              <h3 className="text-sm font-medium text-amber-600 mb-3">Pause / Blocked</h3>
              <div className="grid grid-cols-2 gap-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                {batch.spam_paused && (
                  <InfoItem label="Spam-paused at" value={formatDateTime(batch.spam_paused_at)} className="text-red-700" />
                )}
                {batch.billing_paused && (
                  <InfoItem label="Billing-suspended at" value={formatDateTime(batch.billing_paused_at)} className="text-red-700" />
                )}
                {batch.user_paused && (
                  <InfoItem label="User-paused" value="Yes (contacts_to_tag.tagging_status = 'paused')" className="text-amber-700" />
                )}
              </div>
            </div>
          )}

          {/* CSV Info */}
          {(batch.original_filename || batch.file_size_bytes) && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">CSV Source</h3>
              <div className="grid grid-cols-3 gap-4">
                <InfoItem label="Original filename" value={batch.original_filename || '—'} mono />
                <InfoItem label="File size" value={formatBytes(batch.file_size_bytes)} />
                <InfoItem label="Rows in CSV" value={(batch.total_rows_in_csv ?? 0).toLocaleString()} />
              </div>
            </div>
          )}

          {/* Financial */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Financial</h3>
            <div className="grid grid-cols-3 gap-4">
              <InfoItem label="Revenue" value={`$${revenue.toFixed(4)}`} className="text-green-600" />
              <InfoItem label="Cost" value={`$${cost.toFixed(4)}`} />
              <InfoItem
                label="Profit"
                value={`$${profit.toFixed(4)}`}
                className={profit >= 0 ? 'text-emerald-600' : 'text-red-600'}
              />
              {(estRevenue !== revenue || estCost !== cost) && (
                <>
                  <InfoItem label="Estimated revenue" value={`$${estRevenue.toFixed(4)}`} className="text-gray-500" />
                  <InfoItem label="Estimated cost" value={`$${estCost.toFixed(4)}`} className="text-gray-500" />
                  <div />
                </>
              )}
            </div>
            {(batch.payment_transaction_id || batch.payment_received_at) && (
              <div className="grid grid-cols-2 gap-4 mt-4 pt-3 border-t border-gray-100">
                {batch.payment_transaction_id && (
                  <InfoItem label="Payment transaction" value={batch.payment_transaction_id} mono />
                )}
                {batch.payment_received_at && (
                  <InfoItem label="Payment received" value={formatDateTime(batch.payment_received_at)} />
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <a
              href={`${import.meta.env.VITE_API_URL}/admin/issues?batchId=${batch.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              View Issues
            </a>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Tiny inline pill rendered next to each phase heading in the detail modal,
 * indicating whether the phase is done, running, paused, stuck, or pending.
 */
function PhaseStatePill({ state }: { state: ReturnType<typeof derivePhaseSummary>[number]['state'] }) {
  const toneByState: Record<typeof state, string> = {
    done: 'bg-emerald-100 text-emerald-800',
    running: 'bg-blue-100 text-blue-800',
    counting_stuck: 'bg-red-100 text-red-800',
    activity_stuck: 'bg-amber-100 text-amber-800',
    paused: 'bg-yellow-100 text-yellow-800',
    pending: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${toneByState[state]}`}>
      {phaseStateLabel(state)}
    </span>
  );
}

// Info Item Component
function InfoItem({
  label,
  value,
  mono,
  className,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-sm font-medium text-gray-900 ${mono ? 'font-mono text-xs' : ''} ${className || ''}`}>
        {value}
      </p>
    </div>
  );
}

// ============================================================================
// Phase 4: GHL Tag Writes — list-row cell + click-through detail modal
// ============================================================================

/**
 * Inline cell that summarizes tag-write progress for a batch. Reads only the
 * aggregate counts already attached to the batch row by the list endpoint —
 * no extra network roundtrips per row. Renders as:
 *   "—" when no tag writes exist (no bad contacts in the batch)
 *   "X/Y" with subtle color when in progress / all done / has failures
 *   Hover tooltip shows the queued/processing/completed/failed breakdown.
 */
function TagWritesCell({ batch }: { batch: Batch }) {
  const tw = batch.tag_writes;
  if (!tw || tw.total === 0) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const done = tw.completed + tw.failed;
  const allDone = done >= tw.total;
  const hasFailed = tw.failed > 0;
  const tone = hasFailed
    ? 'text-red-700 bg-red-50 border-red-200'
    : allDone
      ? 'text-green-700 bg-green-50 border-green-200'
      : 'text-amber-800 bg-amber-50 border-amber-200';

  const tooltip = `Queued: ${tw.queued} · Processing: ${tw.processing} · Completed: ${tw.completed} · Failed: ${tw.failed}`;

  return (
    <button
      type="button"
      className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium hover:opacity-80 ${tone}`}
      title={tooltip}
    >
      {tw.completed.toLocaleString()}/{tw.total.toLocaleString()}
      {hasFailed && <span className="ml-1">⚠</span>}
    </button>
  );
}

/**
 * Click-through modal: lists every tag-write row for the selected batch.
 * Fetched on open. Up to 500 rows are returned per the backend cap; the
 * aggregate counts on the row reflect the true total even when truncated.
 */
function TagWritesModal({ batch, onClose }: { batch: Batch; onClose: () => void }) {
  const [data, setData] = useState<TagWritesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/admin/batches/${batch.id}/tag-writes`,
          { credentials: 'include' }
        );
        const json = (await response.json()) as any;
        if (cancelled) return;
        if (json.success) {
          setData({
            batchId: json.batchId,
            tagWrites: json.tagWrites ?? [],
            counts: json.counts,
            truncated: json.truncated,
          });
        } else {
          setError(json.error || 'Failed to fetch tag writes');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Network error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batch.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              GHL Tag Writes — {batch.batch_name}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {batch.location_name || 'Unknown location'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          {data ? (
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-xs text-gray-500">Queued</div>
                <div className="font-medium text-amber-800">{data.counts.queued.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Processing</div>
                <div className="font-medium text-blue-800">{data.counts.processing.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Completed</div>
                <div className="font-medium text-green-800">{data.counts.completed.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Failed</div>
                <div className="font-medium text-red-800">{data.counts.failed.toLocaleString()}</div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">Loading counts...</div>
          )}
          {data?.truncated && (
            <p className="mt-2 text-xs text-amber-700">
              Showing most recent 500 rows. Counts above reflect the full total.
            </p>
          )}
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? (
            <div className="py-12 text-center text-gray-500">Loading...</div>
          ) : error ? (
            <div className="py-12 text-center text-red-600">{error}</div>
          ) : !data || data.tagWrites.length === 0 ? (
            <div className="py-12 text-center text-gray-500">No tag writes for this batch.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Phone</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Tag</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Source</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Retries</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Created</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.tagWrites.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{row.phone}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.tag}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{row.source}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        row.status === 'completed' ? 'bg-green-100 text-green-800' :
                        row.status === 'failed' ? 'bg-red-100 text-red-800' :
                        row.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">{row.retry_count}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {new Date(row.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-xs text-red-700 max-w-xs truncate" title={row.last_error || ''}>
                      {row.last_error || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
